export type BookCostCalc = {
  // do BookCostCalculationResponse
  unitCost: number;                    // custo de produção por unidade (R$)
  platformProfitPercent: number;       // %
  baseTaxPercent: number;              // %
};

export type Breakdown = {
  price: number;            // preço do kit
  variableRate: number;     // 0..1 (ex.: 0.20 = 20%)
  variableCost: number;     // R$ custos percentuais
  productionCost: number;   // R$ produção (0 se não-físico)
  totalCost: number;        // R$ custo total (variável + produção)
  profit: number;           // R$ lucro
  marginPctOnBox: number;   // % sobre o preço do kit
};

export function toMoney(v: number | string | null | undefined): number {
  const n = typeof v === 'string' ? Number(v) : (v ?? 0);
  return Math.round((Number.isFinite(n) ? (n as number) : 0) * 100) / 100;
}

/** Soma dos percentuais variáveis aplicados sobre o preço do kit (0..1) */
export function getVariableRate(calc: BookCostCalc): number {
  const p = toMoney(calc.platformProfitPercent);
  const t = toMoney(calc.baseTaxPercent);
  const r = (p + t) / 100;
  return Math.min(0.95, Math.max(0, r)); // trava por segurança
}

/** Custo de produção fixo (entra só se for livro físico) */
export function getProductionCost(calc: BookCostCalc, hasPhysicalBook: boolean): number {
  return hasPhysicalBook ? toMoney(calc.unitCost) : 0;
}

/** Quebra completa para um preço informado */
export function computeBreakdown(
  calc: BookCostCalc,
  price: number,
  hasPhysicalBook: boolean
): Breakdown {
  const p = toMoney(price);
  const variableRate = getVariableRate(calc);
  const variableCost = toMoney(p * variableRate);
  const productionCost = getProductionCost(calc, hasPhysicalBook);
  const totalCost = toMoney(variableCost + productionCost);
  const profit = toMoney(p - totalCost);
  const marginPctOnBox = p > 0 ? toMoney((profit / p) * 100) : 0;
  return { price: p, variableRate, variableCost, productionCost, totalCost, profit, marginPctOnBox };
}

/** Mínimo permitido para fechar a conta com custos proporcionais + produção (nunca abaixo de 10) */
export function getMinPrice(
  calc: BookCostCalc,
  hasPhysicalBook: boolean,
  hardFloor = 10
): number {
  const prod = getProductionCost(calc, hasPhysicalBook);
  const r = getVariableRate(calc);
  const denom = (1 - r) <= 0 ? 0.01 : (1 - r);
  const minByCost = prod / denom;
  return toMoney(Math.max(hardFloor, minByCost));
}
